import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import PageHeader from '../components/PageHeader';
import ClaimDetailDrawer from '../components/drawers/ClaimDetailDrawer';
import { useAdminData } from '../contexts/AdminDataContext';
import { AIOutlineButton } from '../components/ui/AIOutlineButton';
import { AISidebar } from '../components/AISidebar';
import { AIRecommendationPanel } from '../components/ai/AIRecommendationPanel';
import { Search, Filter, Download, Calendar } from 'lucide-react';
// Mock AI recommendations for claims
const getClaimsRecommendations = () => [{
  id: '1',
  title: 'Picco riscatti cosmetici sostenibili',
  type: 'Opportunità',
  diamondRange: '+25% ultimi 7 giorni',
  reason: 'I riscatti di cosmetici solidi zero-waste sono aumentati del 25% negli ultimi 7 giorni. Considera di aumentare la disponibilità di questi premi.',
  ctaLabel: 'Vedi premi'
}, {
  id: '2',
  title: 'Calo conversione Set Hennetica',
  type: 'Attenzione',
  diamondRange: '-15% conversione',
  reason: 'Il Set Hennetica mostra un calo nella conversione. Valuta una riduzione del costo a 50 crediti o includi una consulenza gratuita.',
  ctaLabel: 'Modifica premio'
}, {
  id: '3',
  title: 'Workshop alta domanda',
  type: 'Trend',
  diamondRange: 'Lista attesa: 30%',
  reason: 'I Workshop Cosmetici Fai-da-Te hanno alta domanda. Aumenta la disponibilità del 30% per soddisfare le richieste.',
  ctaLabel: 'Aumenta disponibilità'
}];
const Claims = () => {
  const navigate = useNavigate();
  const {
    claims
  } = useAdminData();
  const [selectedClaimId, setSelectedClaimId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAISidebarOpen, setIsAISidebarOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('Tutti');
  const [partnerFilter, setPartnerFilter] = useState('Tutti i Partner');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const uniquePartners = useMemo(() => {
    const partners = claims.map(c => c.partner);
    return ['Tutti i Partner', ...Array.from(new Set(partners))];
  }, [claims]);
  const filteredClaims = useMemo(() => {
    return claims.filter(claim => {
      const matchesStatus = statusFilter === 'Tutti' || claim.status === statusFilter;
      const matchesPartner = partnerFilter === 'Tutti i Partner' || claim.partner === partnerFilter;
      const matchesSearch = claim.id.toLowerCase().includes(searchTerm.toLowerCase()) || claim.userId.toLowerCase().includes(searchTerm.toLowerCase());
      let matchesDateFrom = true;
      if (dateFrom) {
        const claimDate = new Date(claim.date.split(' ')[0]);
        const fromDate = new Date(dateFrom);
        matchesDateFrom = claimDate >= fromDate;
      }
      let matchesDateTo = true;
      if (dateTo) {
        const claimDate = new Date(claim.date.split(' ')[0]);
        const toDate = new Date(dateTo);
        matchesDateTo = claimDate <= toDate;
      }
      return matchesStatus && matchesPartner && matchesSearch && matchesDateFrom && matchesDateTo;
    });
  }, [claims, statusFilter, partnerFilter, searchTerm, dateFrom, dateTo]);
  const handleRowClick = (claimId: string) => {
    setSelectedClaimId(claimId);
    setIsDrawerOpen(true);
  };
  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    setTimeout(() => setSelectedClaimId(null), 300);
  };
  const handleExportCSV = () => {
    console.log('Esportazione CSV avviata');
  };
  const recommendations = getClaimsRecommendations().map(rec => ({
    ...rec,
    onCtaClick: () => console.log('CTA clicked:', rec.id)
  }));
  const handlePartnerClick = (e: React.MouseEvent, partnerId: string) => {
    e.stopPropagation();
    navigate(`/partners/${partnerId}`);
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Utilizzato':
        return 'bg-green-100 text-green-800';
      case 'Riscattato':
        return 'bg-blue-100 text-blue-800';
      case 'Annullato':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="flex h-screen bg-gray-50" data-id="element-2021">
      <div className="w-20 flex-shrink-0" data-id="element-2022">
        <Sidebar data-id="element-2023" />
      </div>

      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-2024">
        <Topbar data-id="element-2025" />
        <main className="flex-1 overflow-y-auto p-4 md:p-6" data-id="element-2026">
          {/* Page Header with AI Button */}
          <div className="mb-8" data-id="element-2027">
            <div className="flex items-start justify-between" data-id="element-2028">
              <div data-id="element-2029">
                <h1 className="text-2xl font-semibold text-gray-900" data-id="element-2030">
                  Riscatti
                </h1>
                <p className="text-sm font-normal text-gray-500 mt-1" data-id="element-2031">
                  Storico dei premi riscattati (dati pseudonimi)
                </p>
              </div>
              <div className="flex items-center gap-3" data-id="element-2032">
                <button onClick={handleExportCSV} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors" style={{
                borderRadius: '100px'
              }} data-id="element-2033">
                  <Download className="h-4 w-4 mr-2" data-id="element-2034" />
                  Esporta CSV
                </button>
                <AIOutlineButton onClick={() => setIsAISidebarOpen(true)} data-id="element-2035">
                  Suggerimenti AI
                </AIOutlineButton>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="mb-6 flex flex-wrap gap-3" data-id="element-2036">
            <div className="relative flex-1 min-w-[300px]" data-id="element-2037">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" data-id="element-2038" />
              <input type="text" placeholder="Cerca ID riscatto o ID utente..." className="w-full rounded-lg border border-gray-300 pl-10 pr-4 py-2 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-green-600" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2039" />
            </div>

            {/* Date From */}
            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2 gap-2" data-id="element-2040">
              <Calendar className="h-4 w-4 text-gray-500" data-id="element-2041" />
              <span className="text-sm font-normal text-gray-600" data-id="element-2042">Da:</span>
              <input type="date" className="bg-transparent text-sm font-normal focus:outline-none" value={dateFrom} onChange={e => setDateFrom(e.target.value)} data-id="element-2043" />
            </div>

            {/* Date To */}
            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2 gap-2" data-id="element-2044">
              <Calendar className="h-4 w-4 text-gray-500" data-id="element-2045" />
              <span className="text-sm font-normal text-gray-600" data-id="element-2046">A:</span>
              <input type="date" className="bg-transparent text-sm font-normal focus:outline-none" value={dateTo} onChange={e => setDateTo(e.target.value)} data-id="element-2047" />
            </div>

            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-2048">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-2049" />
              <select className="bg-transparent text-sm font-normal focus:outline-none" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} data-id="element-2050">
                <option value="Tutti" data-id="element-2051">Tutti gli stati</option>
                <option value="Riscattato" data-id="element-2052">Riscattato</option>
                <option value="Utilizzato" data-id="element-2053">Utilizzato</option>
                <option value="Annullato" data-id="element-2054">Annullato</option>
              </select>
            </div>

            <div className="flex items-center rounded-lg border border-gray-300 px-3 py-2" data-id="element-2055">
              <Filter className="mr-2 h-4 w-4 text-gray-500" data-id="element-2056" />
              <select className="bg-transparent text-sm font-normal focus:outline-none" value={partnerFilter} onChange={e => setPartnerFilter(e.target.value)} data-id="element-2057">
                {uniquePartners.map(partner => <option key={partner} value={partner} data-id="element-2058">
                    {partner}
                  </option>)}
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="rounded-lg border border-gray-200 bg-white overflow-hidden" data-id="element-2059">
            <div className="overflow-x-auto" data-id="element-2060">
              <table className="w-full" data-id="element-2061">
                <thead data-id="element-2062">
                  <tr className="bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-2063">
                    <th className="px-6 py-3" data-id="element-2064">ID Riscatto</th>
                    <th className="px-6 py-3" data-id="element-2065">Data/Ora</th>
                    <th className="px-6 py-3 whitespace-nowrap" data-id="element-2066">Partner</th>
                    <th className="px-6 py-3" data-id="element-2067">Premio</th>
                    <th className="px-6 py-3" data-id="element-2068">Crediti</th>
                    <th className="px-6 py-3" data-id="element-2069">Utente</th>
                    <th className="px-6 py-3" data-id="element-2070">Stato</th>
                    <th className="px-6 py-3" data-id="element-2071">Metodo</th>
                    <th className="px-6 py-3 text-right" data-id="element-2072">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200" data-id="element-2073">
                  {filteredClaims.map(claim => <tr key={claim.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleRowClick(claim.id)} data-id="element-2074">
                      <td className="px-6 py-4" data-id="element-2075">
                        <span className="font-mono text-sm font-medium text-gray-900" data-id="element-2076">
                          {claim.id}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-normal text-gray-600" data-id="element-2077">
                        {claim.date}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-2078">
                        <button onClick={e => handlePartnerClick(e, claim.partnerId)} className="text-sm font-medium text-green-600 hover:text-green-700 underline" data-id="element-2079">
                          {claim.partner}
                        </button>
                      </td>
                      <td className="px-6 py-4" data-id="element-2080">
                        <div className="font-medium text-gray-900" data-id="element-2081">
                          {claim.reward}
                        </div>
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900" data-id="element-2082">
                        {claim.diamonds}
                      </td>
                      <td className="px-6 py-4" data-id="element-2083">
                        <span className="font-mono text-sm font-normal text-gray-600" data-id="element-2084">
                          {claim.userId}
                        </span>
                      </td>
                      <td className="px-6 py-4" data-id="element-2085">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(claim.status)}`} data-id="element-2086">
                          {claim.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm font-normal text-gray-600" data-id="element-2087">
                        {claim.method}
                      </td>
                      <td className="px-6 py-4 text-right" data-id="element-2088">
                        <button onClick={e => {
                      e.stopPropagation();
                      handleRowClick(claim.id);
                    }} className="text-green-600 hover:text-green-700 text-sm font-medium" data-id="element-2089">
                          Dettagli
                        </button>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {filteredClaims.length === 0 && <div className="text-center py-12 text-gray-500" data-id="element-2090">
              <p className="text-sm font-normal text-gray-500" data-id="element-2091">
                Nessun riscatto trovato con i filtri selezionati.
              </p>
            </div>}
        </main>
      </div>

      {/* AI Sidebar */}
      <AISidebar isOpen={isAISidebarOpen} onClose={() => setIsAISidebarOpen(false)} title="Suggerimenti AI: Riscatti" data-id="element-2092">
        <AIRecommendationPanel recommendations={recommendations} data-id="element-2093" />
      </AISidebar>

      {/* Detail Drawer */}
      <ClaimDetailDrawer isOpen={isDrawerOpen} onClose={handleCloseDrawer} claimId={selectedClaimId} data-id="element-2094" />
    </div>;
};
export default Claims;